import { ng, JsonNumber } from "../ngds";

// Depends on ngds alone (no protoGen/funlib, which pull in .tsx), so an app server or a Node print
// service can import it as it is.
//
// Embodied carbon (A1-A3) of a project, the part every app shares: which design service computes it,
// asking it, deciding when a stored figure is out of date, and reading the result for display. The
// apps only wire it to their own state (Redux in sx-column, plain context in sx-beam) and to their own
// gateway helper.
//
// The design services compute the footprint per element, one ElementCarbonFootprint each, and sum the
// volume over all segments of an element — so nothing here assumes one element or one segment.
// RC-Design covers concrete elements, FEM-Design steel and timber; each skips the elements it does not
// cover, so a project mixing materials is answered by asking both and concatenating.

/** Carbon footprint of one element, in the JSON shape a project stores it in (`output.carbon`). */
export type ElementCarbonJson = ng.environmental_parameters.ElementCarbonFootprint_json;
export type CarbonFootPrintInputJson = ng.endpoint_args.common_api_functions.CarbonFootPrintInput_json;
export type CarbonFootPrintOutputJson = ng.endpoint_args.common_api_functions.CarbonFootPrintOutput_json;

/** State of a standalone carbon calculation. Only "loading" changes what a readout shows. */
export type CarbonStatus = "idle" | "loading" | "succeeded" | "failed";

// ------------------------------------------------------------------------------------------------
//                                   Which service, and asking it
// ------------------------------------------------------------------------------------------------

/** Gateway paths of the carbon endpoints, without the app's license prefix (`/beam`, `/column`). */
export const RC_CARBON_PATH = "/rc-design/carbonfootprint";
export const FEM_CARBON_PATH = "/fem-design-api/carbonfootprint";

/**
 * The design service that computes the footprint of an element of this material — the same split the
 * code check makes. Null when no service does (masonry, custom, an unknown material).
 */
export const carbonPathForMaterial = (material: ng.material.Mtrl | null | undefined): string | null => {
  switch (material) {
    case ng.material.Mtrl.MTRL_CONCRETE:
      return RC_CARBON_PATH;
    case ng.material.Mtrl.MTRL_STEEL:
    case ng.material.Mtrl.MTRL_TIMBER:
      return FEM_CARBON_PATH;
    default:
      return null;
  }
};

/**
 * The material of every beam element in the input, by `input.mtrlDb[guid == beam.mtrlGuid].type` —
 * the authority the design dispatch routes on, not the beam's concrete/steel/timber oneof.
 */
export const elementMaterials = (input: ng.input.Data): ng.material.Mtrl[] => {
  const materials: ng.material.Mtrl[] = [];
  for (const structure of input.structures) {
    for (const element of structure.elements) {
      const beam = element.getBeam();
      if (!beam) continue;
      const mtrl = input.mtrlDb.find((m) => m.id?.guid === beam.mtrlGuid);
      materials.push(mtrl?.type ?? ng.material.Mtrl.MTRL_UNSPECIFIED);
    }
  }
  return materials;
};

/** The distinct carbon endpoints a project's elements need, in a stable order (RC first). */
export const carbonPathsForInput = (input: ng.input.Data): string[] => {
  const paths = new Set(elementMaterials(input).map(carbonPathForMaterial));
  return [RC_CARBON_PATH, FEM_CARBON_PATH].filter((path) => paths.has(path));
};

/** Does any element of this input have a material a carbon service covers? */
export const hasCarbonService = (input: ng.input.Data): boolean => carbonPathsForInput(input).length > 0;

/** An empty footprint, in the shape a carbon endpoint answers with. */
export const emptyCarbonOutput = (): CarbonFootPrintOutputJson =>
  ng.endpoint_args.common_api_functions.CarbonFootPrintOutput.toObject(
    ng.endpoint_args.common_api_functions.CarbonFootPrintOutput.create({}),
  )!;

/**
 * Server side: computes the footprint of every element by asking each design service the input needs
 * and concatenating their answers. `post` sends one request through the app's own gateway helper
 * (with the app's license prefix) and resolves with the service's JSON, or rejects — a failure of
 * either service fails the whole calculation, since half a footprint read as the whole one is worse
 * than none.
 */
export async function calculateCarbonFootprint(
  body: CarbonFootPrintInputJson,
  post: (path: string, body: CarbonFootPrintInputJson) => Promise<CarbonFootPrintOutputJson>,
): Promise<CarbonFootPrintOutputJson> {
  const paths = carbonPathsForInput(ng.input.Data.fromObject(body.input ?? {}));
  const answers = await Promise.all(paths.map((path) => post(path, body)));

  const merged = emptyCarbonOutput();
  merged.carbon = answers.flatMap((answer) => answer?.carbon ?? []);
  merged.log = { entries: answers.flatMap((answer) => answer?.log?.entries ?? []) };
  return merged;
}

/**
 * Client side: asks the app server's carbon route (`url`, e.g. `/api/projects/carbonfootprint`) for
 * the footprint of `input`. The headers are the app's (authorization, x-client-id).
 *
 * The answer is round-tripped through the generated class, which normalises enums and defaults, so it
 * reads the same as the carbon a code check returns. Log entries are only logged: a missing emission
 * factor already shows as `hasMissingData` on the result.
 */
export async function fetchCarbonFootprint(
  url: string,
  input: ng.input.Data_json,
  headers: Record<string, string>,
  signal?: AbortSignal,
): Promise<ElementCarbonJson[]> {
  const response = await fetch(url, {
    method: "POST",
    headers: { ...headers, "Content-Type": "application/json" },
    body: JSON.stringify({ input } as CarbonFootPrintInputJson),
    signal,
  });
  if (!response.ok) {
    throw new Error(`carbon footprint failed: ${response.status} ${await response.text().catch(() => "")}`);
  }
  const out = ng.endpoint_args.common_api_functions.CarbonFootPrintOutput.fromObject(await response.json());
  const errors = (out.log?.entries ?? []).filter((e) => e.type === ng.log.LogType.LOG_TYPE_ERROR);
  if (errors.length > 0) console.warn("carbon footprint returned log entries:", errors);
  return ng.endpoint_args.common_api_functions.CarbonFootPrintOutput.toObject(out)?.carbon ?? [];
}

// ------------------------------------------------------------------------------------------------
//                                   When a figure is out of date
// ------------------------------------------------------------------------------------------------

/**
 * A key that changes whenever an input the footprint is calculated from changes: per element its
 * whole beam (segments and their lengths, sections, material, reinforcement) and the material and
 * section entries it references. Two inputs with the same key have the same footprint.
 *
 * Deliberately coarse — the whole beam rather than a hand-picked list of fields — so a field the
 * services start to read cannot be missed. An edit that turns out not to matter (a cover, a fire
 * setting) costs one needless recalculation, which is cheap.
 */
export const carbonInputKey = (input: ng.input.Data_json | null | undefined): string => {
  if (!input) return "";
  const mtrlDb = input.mtrlDb ?? [];
  const secDb = input.secDb ?? [];
  const elements = (input.structures ?? []).flatMap((s) => s.elements ?? []).filter((e) => e.beam);
  const used = new Set<string>();
  for (const element of elements) {
    const beam = element.beam!;
    if (beam.mtrlGuid) used.add(beam.mtrlGuid);
    for (const seg of beam.segments ?? []) {
      if (seg.layer?.start?.secGuid) used.add(seg.layer.start.secGuid);
      if (seg.layer?.end?.secGuid) used.add(seg.layer.end.secGuid);
    }
    // Reinforcement materials are only referenced from inside the beam; collect them generically.
    JSON.stringify(beam, (key, value) => {
      if (key === "mtrlGuid" && typeof value === "string") used.add(value);
      return value;
    });
  }
  return JSON.stringify({
    elements: elements.map((e) => ({ guid: e.id?.guid, beam: e.beam })),
    mtrlDb: mtrlDb.filter((m) => used.has(m.id?.guid ?? "")),
    secDb: secDb.filter((s) => used.has(s.id?.guid ?? "")),
  });
};

/**
 * Does this carbon result actually state a figure? An entry with no items and a zero total (a service
 * that ran but had nothing to report) does not, and must not stop a recalculation.
 */
export const carbonHasFigure = (carbon: ElementCarbonJson[] | null | undefined): boolean =>
  (carbon ?? []).some((element) => {
    const footprint = element?.carbon;
    if (!footprint) return false;
    return (footprint.items?.length ?? 0) > 0 || (typeof footprint.gwp === "number" && footprint.gwp !== 0);
  });

// ------------------------------------------------------------------------------------------------
//                                          Display
// ------------------------------------------------------------------------------------------------

/** proto3 doubles arrive as `number` or as one of the non-finite string literals. */
export const carbonNum = (v: JsonNumber | undefined): number => (typeof v === "number" ? v : 0);

/**
 * Adaptive precision — an element is normally a few hundred kg CO2e, but a small timber section can
 * land under 10 and a long beam over 1000, and in either case we want a readable number of digits.
 */
export const formatKg = (kg: number): string => {
  const abs = Math.abs(kg);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 2;
  return kg.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

/** Emission factors span several orders of magnitude (0.745 for rebar, 411 for concrete). */
export const formatFactor = (factor: number): string => {
  const abs = Math.abs(factor);
  const digits = abs >= 100 ? 0 : abs >= 10 ? 1 : 3;
  return factor.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });
};

export const formatQuantity = (quantity: number): string =>
  quantity.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 2 });

/** Denominator of the emission factor, and unit of the quantity it was applied to. */
export const declaredUnitLabel = (unit: string | undefined): string => {
  switch (unit) {
    case "DECLARED_UNIT_KG":
      return "kg";
    case "DECLARED_UNIT_M3":
      return "m³";
    case "DECLARED_UNIT_M2":
      return "m²";
    case "DECLARED_UNIT_M":
      return "m";
    default:
      return "";
  }
};

/**
 * Translation key of a material category. Reuses the keys the apps already have for these materials
 * rather than introducing carbon-specific ones.
 */
export const materialLabelKey = (type: string | undefined): string => {
  switch (type) {
    case "MTRL_CONCRETE":
      return "concrete_";
    case "MTRL_REINFORCEMENT":
      return "reinforcement_";
    case "MTRL_STEEL":
      return "steel_";
    case "MTRL_TIMBER":
      return "timber_";
    case "MTRL_MASONRY":
      return "masonry_";
    case "MTRL_FIRE_PROTECTION_STEEL":
    case "MTRL_FIRE_PROTECTION_TIMBER":
      return "fire_protection_material_";
    default:
      return "material_";
  }
};

/**
 * Colour per material category, so the share bar can be read without the legend once the user has
 * seen it expanded once. CSS variables (see styling/scss/components/SXCarbonFootprint.scss), so the
 * themes stay in control of the actual colours.
 */
export const materialColorVar = (type: string | undefined): string => {
  switch (type) {
    case "MTRL_CONCRETE":
      return "var(--sx-carbon-concrete)";
    case "MTRL_REINFORCEMENT":
      return "var(--sx-carbon-reinforcement)";
    case "MTRL_STEEL":
      return "var(--sx-carbon-steel)";
    case "MTRL_TIMBER":
      return "var(--sx-carbon-timber)";
    default:
      return "var(--sx-carbon-other)";
  }
};

/** Contribution of one material, flattened out of the proto for rendering. */
export interface CarbonRow {
  key: string;
  type: string | undefined;
  materialName: string;
  quantity: number;
  unit: string | undefined;
  factor: number;
  gwp: number;
  /** Fraction of the total, 0-1. Used for the share bar and the % column. */
  share: number;
  color: string;
}

export interface CarbonSummary {
  gwp: number;
  gwpBiogenic: number;
  hasMissingData: boolean;
  source: string;
  rows: CarbonRow[];
}

/**
 * Sums the per-element footprints into one readout for the whole project.
 *
 * Items of the same material category *and* the same specific material are merged, so two spans of
 * C30/37 read as one concrete row.
 */
export const summariseCarbon = (carbon: ElementCarbonJson[] | null | undefined): CarbonSummary => {
  let gwp = 0;
  let gwpBiogenic = 0;
  let hasMissingData = false;
  const sources = new Set<string>();
  const merged = new Map<string, CarbonRow>();

  for (const element of carbon ?? []) {
    const footprint = element?.carbon;
    if (!footprint) continue;

    gwp += carbonNum(footprint.gwp);
    gwpBiogenic += carbonNum(footprint.gwpBiogenic);
    hasMissingData = hasMissingData || footprint.hasMissingData === true;
    if (footprint.source) sources.add(footprint.source);

    for (const item of footprint.items ?? []) {
      const materialName = item.materialId?.name ?? "";
      const key = `${item.type ?? ""}|${item.materialId?.guid ?? materialName}`;
      const existing = merged.get(key);
      if (existing) {
        existing.quantity += carbonNum(item.quantity);
        existing.gwp += carbonNum(item.gwp);
      } else {
        merged.set(key, {
          key,
          type: item.type,
          materialName,
          quantity: carbonNum(item.quantity),
          unit: item.unit,
          factor: carbonNum(item.factor),
          gwp: carbonNum(item.gwp),
          share: 0,
          color: materialColorVar(item.type),
        });
      }
    }
  }

  // Shares are taken against the summed item contributions rather than against `gwp`, so the bar
  // fills the full width even if the two disagree (they should not, but a missing-data run can).
  const itemTotal = [...merged.values()].reduce((sum, row) => sum + row.gwp, 0);
  const rows = [...merged.values()].sort((a, b) => b.gwp - a.gwp);
  for (const row of rows) {
    row.share = itemTotal > 0 ? row.gwp / itemTotal : 0;
  }

  return { gwp, gwpBiogenic, hasMissingData, source: [...sources].join(" · "), rows };
};
